import LegalPage from "@/components/site/LegalPage";
import { Tent, MapPin, Award, Compass, TrendingUp, Leaf, Users, Sparkles } from "lucide-react";

const camperActions = [
  "Create your personal digital camping passport.",
  "Check in at participating campsites.",
  "Collect unique digital campsite stamps.",
  "Unlock achievements and camping milestones.",
  "Track your camping history and progress.",
  "Discover new camping destinations across the country.",
  "Build your camping profile and experience level.",
];

const ownerActions = [
  "Register your campsite.",
  "Issue official digital campsite stamps.",
  "Verify camper check-ins.",
  "View visitor statistics and campsite activity.",
  "Increase your campsite's visibility within the camping community.",
  "Engage with returning campers.",
];

const steps = [
  { n: 1, t: "Create your Camping Passport account." },
  { n: 2, t: "Explore and visit participating campsites." },
  { n: 3, t: "Check in using the campsite's QR Code or approved verification method." },
  { n: 4, t: "Receive an official digital campsite stamp." },
  { n: 5, t: "Build your passport, unlock achievements, and continue exploring new destinations." },
];

const reasons = [
  { icon: Award, title: "Digital Passport Experience", body: "Keep a lifelong record of your camping adventures without worrying about losing paper records." },
  { icon: Tent, title: "Verified Campsite Check-ins", body: "Digital stamps are issued only by participating campsite operators through secure verification." },
  { icon: TrendingUp, title: "Gamified Experience", body: "Earn achievements, level up your camping profile, and collect exclusive campsite stamps." },
  { icon: Compass, title: "Discover More Campsites", body: "Explore new destinations and expand your outdoor adventures." },
  { icon: Users, title: "Support Local Campsites", body: "Help promote participating campsites while strengthening Malaysia's camping community." },
];

const values = [
  { n: "1.1", title: "Adventure", body: "We believe every campsite tells a story worth remembering." },
  { n: "1.2", title: "Community", body: "Camping brings people together. We strive to build a welcoming community that connects campers and campsite operators." },
  { n: "1.3", title: "Authenticity", body: "Every verified digital stamp represents a genuine camping experience." },
  { n: "1.4", title: "Sustainability", body: "We encourage responsible camping practices and respect for nature to help preserve outdoor spaces for future generations." },
  { n: "1.5", title: "Innovation", body: "We use technology to enhance the camping experience while keeping the spirit of outdoor adventure alive." },
];

export default function About() {
  return (
    <LegalPage
      eyebrow="About Us"
      title="Your Digital Passport to Every Camping Adventure"
      subtitle="MyAdventure-Passport is a digital platform created for camping enthusiasts and campsite operators to connect through a unique digital passport experience."
    >
      <p className="mb-5">
        Our mission is simple: make every camping adventure memorable by
        allowing campers to collect verified digital campsite stamps, track
        their outdoor journeys, and celebrate their achievements — all from a
        single mobile application.
      </p>
      <p className="mb-8">
        Whether you're a first-time camper or an experienced outdoor explorer,
        MyAdventure-Passport transforms every campsite visit into a lasting
        digital memory.
      </p>

      <div className="grid md:grid-cols-2 gap-5 mb-10 not-prose">
        <div
          className="p-6 rounded-2xl"
          style={{ background: "var(--surface-secondary)", border: "1px solid var(--border-c)" }}
        >
          <div className="eyebrow mb-3">Our Mission</div>
          <p className="body-lg" style={{ color: "var(--on-surface)" }}>
            To inspire more people to embrace the outdoors by creating a
            trusted, engaging, and connected camping community through
            innovative digital experiences.
          </p>
        </div>
        <div
          className="p-6 rounded-2xl"
          style={{ background: "var(--surface-secondary)", border: "1px solid var(--border-c)" }}
        >
          <div className="eyebrow mb-3">Our Vision</div>
          <p className="body-lg" style={{ color: "var(--on-surface)" }}>
            To become the leading digital camping passport platform that
            connects campers and campsite operators while promoting
            sustainable outdoor recreation and responsible camping.
          </p>
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-3 mt-8">What You Can Do</h2>
      <div className="grid md:grid-cols-2 gap-6 mb-10 not-prose">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5" style={{ color: "var(--brand)" }} />
            <span className="font-extrabold" style={{ color: "var(--on-surface)" }}>As a Camper</span>
          </div>
          <ul className="space-y-2 list-disc pl-6" style={{ color: "var(--on-surface)" }}>
            {camperActions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Tent className="w-5 h-5" style={{ color: "var(--brand)" }} />
            <span className="font-extrabold" style={{ color: "var(--on-surface)" }}>As a Campsite Operator</span>
          </div>
          <ul className="space-y-2 list-disc pl-6" style={{ color: "var(--on-surface)" }}>
            {ownerActions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-4 mt-8">How Camping Passport Works</h2>
      <div className="space-y-4 mb-10 not-prose">
        {steps.map((s) => (
          <div key={s.n} className="flex items-start gap-4">
            <span
              className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-extrabold"
              style={{ background: "var(--premium)", color: "#fff" }}
            >
              {s.n}
            </span>
            <p className="pt-1" style={{ color: "var(--on-surface)" }}>{s.t}</p>
          </div>
        ))}
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-4 mt-8">Why Choose Camping Passport?</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-10 not-prose">
        {reasons.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="p-5 rounded-2xl"
            style={{ background: "var(--surface-secondary)", border: "1px solid var(--border-c)" }}
          >
            <Icon className="w-5 h-5 mb-3" style={{ color: "var(--brand)" }} />
            <div className="font-extrabold mb-1" style={{ color: "var(--on-surface)" }}>{title}</div>
            <p className="text-sm" style={{ color: "var(--on-surface-muted)" }}>{body}</p>
          </div>
        ))}
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-4 mt-8">Coverage</h2>
      <p className="mb-4">
        MyAdventure-Passport is continuously expanding across Malaysia. Our
        goal is to connect campers with campsites nationwide, making it easier
        than ever to discover new outdoor destinations and document every
        adventure.
      </p>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-4 mt-8">Our Values</h2>
      <div className="space-y-4 mb-10 not-prose">
        {values.map((v) => (
          <div key={v.n} className="flex items-start gap-4">
            <span className="font-mono font-bold shrink-0 w-12" style={{ color: "var(--premium)" }}>
              {v.n}
            </span>
            <div>
              <div className="font-extrabold" style={{ color: "var(--on-surface)" }}>{v.title}</div>
              <p style={{ color: "var(--on-surface-muted)" }}>{v.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold mb-4 mt-8">Join Our Community</h2>
      <p className="mb-4">
        Whether you're collecting your very first campsite stamp or adding
        another destination to your growing passport, MyAdventure-Passport is
        here to accompany every step of your outdoor journey.
      </p>
      <p className="mb-2">
        Start exploring, collect unforgettable memories, and make every camping
        adventure count.
      </p>
      <p className="font-serif italic" style={{ color: "var(--brand)" }}>
        Collect Every Adventure. One Stamp at a Time.
      </p>
    </LegalPage>
  );
}
